import React from "react";
import { View, ScrollView, ImageBackground, StyleSheet } from "react-native";
import moment from "moment";
import { isEqual, sortBy } from "lodash";

import Footer from "../common/footer/index";
import GoalsList from "./goals/GoalsList";
import ResolutionFabButton from "./ResolutionFabButton";
import LoadingIndicator from "../common/LoadingIndicator";

import ResolutionTimeMachine from "./ResolutionTimeMachine";
import Utils, { isWeb, navigate } from "../../../helpers/utils";
import AppMessages from "../../../config/AppMessages";
import appStyles from "../../appStyles";
import GoalBadgeCard from "./GoalBadgeCard";
import TutorialsHelper from "../tutorials/TutorialsHelper";
import TUTORIALS_DATA from "../tutorials/data";
import Header from "../common/header";
import MedalIcon from "../common/icons/MedalIcon";
import GoalsHelper from "./GoalsHelper";
import SettingsHelper from "../more/SettingsHelper";
import GenericOverlay from "../empty-pages/GenericOverlay";
import EmptyPageIcons from "../common/icons/empty-pages/index";

const DATE_FORMAT = "YYYY-MM-DD";
const SyncInProgressMessage = AppMessages.SyncInProgress;

export default class ResolutionPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;

    this.settingsStorageKey = SettingsHelper.getPreferencesStorageKey(
      this.controller,
    );

    this.state = {
      isLoading: true,
      showDateDialog: false,
      selectedDate: false,
      gems: [],
      goals: [],
    };
  }

  componentDidMount = () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.componentDidFocus();
        },
      );
    }

    //this.getResolutions();
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  componentDidFocus = async () => {
    this.getResolutions();
  };

  getArgs() {
    if (this.controller.hasNavigation()) {
      return this.props.route.params;
    } else {
      return this.controller.state.screenArgs?.args?.args;
    }
  }

  getResolutions = async (userSelectedDate) => {
    const isTutorialMode = this.initTutorials();
    // Don't load any goals before tutorials
    if (!isTutorialMode) return;

    const selectedDate = this.getSelectedDate(userSelectedDate);

    const params = selectedDate
      ? {
          resolutionsDate: selectedDate.format(DATE_FORMAT),
        }
      : null;
    const oldResolutionsData = this.controller.state?.resolutions || {};

    // Loading Mari message
    this.setState((state) => ({
      ...state,
      isLoading: false,
      selectedDate: userSelectedDate,
      showTutorialOverlay: !SettingsHelper.wasTutorialOverlaySeen(
        this.controller,
        "goals",
      ),
      mariMessage: state.showTutorials
        ? state.mariMessage
        : SyncInProgressMessage,
      ...this._getSortedGoals(oldResolutionsData, userSelectedDate),
    }));

    // Get goals from API
    this.controller.getApiData("resolutions", params, (resolutions) => {
      if (isEqual(oldResolutionsData, resolutions)) {
        this.setState((state) => ({
          ...state,
          mariMessage: state.showTutorials ? state.mariMessage : "",
        }));
      } else {
        this.setState((state) => ({
          ...state,
          mariMessage: state.showTutorials ? state.mariMessage : "",
          ...this._getSortedGoals(resolutions, userSelectedDate),
        }));
      }
    });
  };

  _getSortedGoals = (resolutions, selectedDate) => {
    const { gems, items, day } = resolutions || {};

    const usedDate = this.getSelectedDate(selectedDate);
    const goals = sortBy(
      (items || []).filter(
        (i) =>
          true ||
          i.from == null ||
          moment(i.from) >= usedDate ||
          i.to == null ||
          moment(i.to) >= usedDate,
      ),
      "from",
    );

    return { goals, day, gems };
  };

  getSelectedDate(userSelectedDate) {
    const selectedDate = userSelectedDate
      ? moment(userSelectedDate)
      : Utils.nowUserTz(this.controller);
    return selectedDate;
  }

  filterResolutionsByDate = (selectedDate) => {
    this.toggleDatePicker();
    this.getResolutions(selectedDate);
  };

  toggleDatePicker = () => {
    // Return to present
    if (this.state.selectedDate) {
      this.getResolutions("");
    } else {
      this.setState((state) => ({
        ...state,
        showDateDialog: !this.state.showDateDialog,
      }));
    }
  };

  addNewGoal = () => {
    const { showTutorials } = this.state;

    const goal = GoalsHelper.getGoalModel({ showTutorials });

    this.controller.changePage("feed", {
      selectedPage: "rn:resolution_detail",
      args: { goal },
    });
  };

  viewAllGems = () => {
    const { gems } = this.state;
    navigate(this.controller, "resolution_gems", "", { gems });
  };

  // ------------------------------------- Tutorials -------------------------------------
  initTutorials = () => {
    const showTutorials = (this.controller?.state?.showTutorials || {})[
      TutorialsHelper.STORAGE_KEYS.goals
    ];

    const { tutorialDone } = this.getArgs() || {};
    const tutorial = TUTORIALS_DATA["resolution"][tutorialDone ? 1 : 0];

    if (showTutorials && tutorial) {
      this.setState((state) => ({
        ...state,
        isLoading: false,
        showTutorials,
        tutorialDone,
        mariMessage: tutorial.message,
        mariAction: tutorial.action
          ? {
              text: tutorial.action.text,
              onPress: () => this.onGoalTutorialDone(),
            }
          : null,
      }));

      return tutorialDone;
    }

    this.setState((state) => ({
      ...state,
      showTutorials: false,
      showBadgeCard: false,
      mariMessage: "",
      mariAction: null,
      goals: [],
    }));

    return true;
  };

  onBadgePress = () => {
    this.setState((state) => ({
      ...state,
      mariMessage: "",
      mariAction: null,
      showBadgeCard: false,
    }));
    this.controller.toggleTutorialsMode();

    TutorialsHelper.saveTutorialStatus(this.controller, { goals: true }, () => {
      this.controller.changePage("rn:resolution", { tutorialDone: false });
      this.controller.changePage("rn:home_card", {});
    });
  };

  onGoalTutorialDone = () => {
    this.setState((state) => ({
      ...state,
      mariMessage: "",
      mariAction: null,
      showBadgeCard: true,
      showTutorials: false,
    }));
  };

  closeTutorialOverlay = () => {
    this.setState((state) => ({ ...state, showTutorialOverlay: false }));
    SettingsHelper.updateTutorialStatus(this.controller, { goals: true });
  };

  aboutThisScreen = () => {
    this.setState((state) => ({ ...state, showTutorialOverlay: true }));
  };

  render() {
    const {
      isLoading,
      showDateDialog,
      selectedDate,
      goals,
      mariMessage,
      showTutorials,
      tutorialDone,
      mariAction,
      showBadgeCard,
      showTutorialOverlay,
    } = this.state;

    return (
      <>
        <GoalBadgeCard isOpen={showBadgeCard} onPress={this.onBadgePress} />

        <View style={appStyles.container}>
          <ImageBackground
            source={require("../../../assets/integrations/MorganBG.png")}
            style={styles.imageContainer}
            imageStyle={styles.image}
          />

          {showTutorialOverlay ? (
            <GenericOverlay
              ImageTag={EmptyPageIcons.GoalsOverlay}
              onButtonPress={this.closeTutorialOverlay}
            />
          ) : null}

          <Header
            title={selectedDate ? "Time-Machine" : "Goals"}
            controller={this.controller}
            onGoBack={selectedDate ? () => {} : null}
            style={{
              minHeight: 88,
            }}
            iconTag={
              !selectedDate ? <MedalIcon onPress={this.viewAllGems} /> : null
            }
          />
          {isLoading ? (
            <LoadingIndicator isLoading={true} />
          ) : (
            <ScrollView
              style={[
                appStyles.scrollView,
                {
                  paddingTop: 8,
                  marginTop: 0,
                  backgroundColor: "transparent",
                  flexGrow: 0,
                  paddingHorizontal: 0,
                },
              ]}
              contentContainerStyle={appStyles.scrollViewContent}
            >
              <GoalsList
                controller={this.controller}
                goals={goals}
                onGoalCreated={this.getResolutions}
              />
            </ScrollView>
          )}
          <ResolutionTimeMachine
            controller={this.controller}
            selectedDate={selectedDate}
            showDateDialog={showDateDialog}
            toggleDatePicker={this.toggleDatePicker}
            onDateSelected={this.filterResolutionsByDate}
          />

          <View style={appStyles.footerOptions}>
            <ResolutionFabButton
              selectedDate={selectedDate}
              style={appStyles.fab}
              addNewGoal={this.addNewGoal}
              showDateSelector={() => this.toggleDatePicker(true)}
              viewAllGems={this.viewAllGems}
              aboutThisScreen={this.aboutThisScreen}
              mariOptions={{
                mariMessage,
                mariAction: mariAction ? [mariAction] : null,
                showTutorials,
                showOverlay: showTutorials && !tutorialDone,
                isSelected: showTutorials && !tutorialDone,
              }}
            />
          </View>

          {this.controller.hasNavigation() ? null : (
            <View style={appStyles.footerOptions}>
              <Footer controller={this.controller} selectedPage="resolution" />
            </View>
          )}
        </View>
      </>
    );
  }
}

const styles = StyleSheet.create({
  imageContainer: {
    position: "absolute",
    height: 439,
    width: 375,
    left: 0,
    right: 0,
    bottom: 40,
    overflow: "hidden",
  },
  image: {
    width: "100%",
    height: 439,
    resizeMode: "contain",
    left: -35,
  },
});
